import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, getDoc, addDoc, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CATEGORIES_DOC_ID = 'main'; // single doc for all categories

const defaultTemplate = {
  name: '',
  scenarioCategory: '',
  description: '',
  rows: [
    { objective: '', notes: '', critical: false },
  ],
};

const defaultTemplates = [
  {
    name: "Oxygen Tank",
    description: "Oxygen Tank Setup + O2 Administration",
    rows: [
      { objective: 'Explain how to identify an Oxygen Tank', notes: 'O2 Tank is labeled oxygen, O2 Tank is green, O2 Tank and regulator use the Pin-Index Safety System', critical: false },
      { objective: 'Explain the purpose of the Regulator', notes: 'Regulates the pressure', critical: false },
      { objective: 'Explain the purpose Flowmeter', notes: 'Controls delivery and flow of oxygen', critical: false },
      { objective: 'Inspect the O-Ring', notes: 'Checking for presence and damages', critical: true },
      { objective: 'Attach Regulator to O2 Tank (hand tight)', notes: 'Must attach correctly, no leaks', critical: true },
      { objective: 'Open the Oxygen Tank properly', notes: 'All the way open, then a half-turn back', critical: false },
      { objective: 'Explain what makes the O2 Tank "full"', notes: 'Around 2,000 PSI', critical: false },
      { objective: 'States Minimum Oxygen Requirement', notes: '500 PSI or greater', critical: true }
    ]
  },
  {
    name: "Nasal Cannula",
    description: "Nasal Cannula Skill Sheet",
    rows: [
      { objective: 'Attach Nasal Cannula to Regulator', notes: 'To Christmas Tree Adapter (CTA)', critical: false },
      { objective: 'State dose of Oxygen to be administered', notes: '1-6 LPM', critical: true },
      { objective: 'State O2% of Nasal Cannula', notes: '24-44%', critical: false },
      { objective: 'Place patient on Nasal Cannula', notes: 'With prongs facing correctly in the nose, then wrapping the ears', critical: false }
    ]
  },
  {
    name: "Non-Rebreather",
    description: "Non-Rebreather Skill Sheet",
    rows: [
      { objective: 'Detach Cannula and attach Nonrebreather', notes: 'To Christmas Tree Adapter (CTA)', critical: false },
      { objective: 'State dose of Oxygen to be administered', notes: '10-15 LPM', critical: true },
      { objective: 'State O2% of Nonrebreather', notes: '80-90%', critical: false },
      { objective: 'Prefill Reservoir Bag', notes: 'Cover exhaust port with thumb', critical: true },
      { objective: 'Place patient on Nonrebreather', notes: 'Place on patients face and adjust nose clamp and straps', critical: true },
      { objective: 'Disassemble O2 Tank without leaks', notes: 'Bleeds out remaining oxygen and detaches regulator', critical: false }
    ]
  },
  {
    name: "Oropharyngeal Airway (OPA)",
    description: "Skill sheet for OPA insertion and management.",
    rows: [
      { objective: "Perform a Manual Airway Maneuver", notes: "Student should perform a head-tilt chin lift", critical: false },
      { objective: "Select and measure an OPA", notes: "From earlobe to corner of the mouth", critical: true },
      { objective: "State contraindications of OPA", notes: "Presence of a Gag-Reflex", critical: true },
      { objective: "Check for presence of Gag-Reflex", notes: "By touching the patient's eyelashes to check for response.", critical: true },
      { objective: "Demonstrate technique for an Adult", notes: "Tip facing upward: Using the roof of the mouth as a guide then rotating in place so the flange rests against the mouth", critical: false },
      { objective: "Demonstrate technique for a Child", notes: "Tip facing downward: Place the OPA straight in", critical: false },
      { objective: "Begins Manual Ventilation", notes: "At rate of 1 breath every 6 seconds looking for chest rise", critical: true }
    ]
  },
  {
    name: "Nasopharyngeal Airway (NPA)",
    description: "Skill sheet for NPA insertion and management.",
    rows: [
      { objective: "Place the head in the Neutral Position", notes: "Student should perform a head-tilt chin lift", critical: false },
      { objective: "State contraindications of NPA", notes: "Signs of significant facial trauma", critical: true },
      { objective: "Select and measure an NPA", notes: "Measure from earlobe to tip of the nose", critical: true },
      { objective: "Apply water-based lubricant to the NPA", notes: "Must apply to outside of the NPA", critical: true },
      { objective: "Insert NPA into the right nostril first", notes: "With bevel toward the septum", critical: false },
      { objective: "Advance NPA to proper depth", notes: "Should advance gently until the trumpet sits at the nare. Instruct the student to demonstrate the left nostril", critical: false },
      { objective: "Remove NPA & insert in the left nostril", notes: "Bevel toward the septum, halfway in, then rotates in place", critical: false },
      { objective: "Attempt to ventilate the patient", notes: "At rate of 1 breath every 6 seconds looking for chest rise", critical: false }
    ]
  },
  {
    name: "Suction",
    description: "Skill sheet for airway suctioning procedure.",
    rows: [
      { objective: "Scene Safety", notes: "Your scene is safe.", critical: false },
      { objective: "BSI or PPE", notes: "What PPE are you choosing now? What would make you consider using more?", critical: true },
      { objective: "Place patient in the recovery position", notes: "Student must verbalize placing the patient in the recovery (recumbent) position. (There is no trauma.)", critical: true },
      { objective: "Set up the disassembled suction unit", notes: "Unit must be correctly reassembled, including the catheter.", critical: true },
      { objective: "Turn on the suction unit and check for the presence of suction to achieve at least 300 mmHg", notes: "Student should test the suction presence and power by clamping the suction tubing.", critical: true },
      { objective: "Inserts catheter with hole uncovered: Rigid Tip", notes: "Must demonstrate using the Rigid Tip", critical: false },
      { objective: "Inserts catheter with hole uncovered: French Catheter", notes: "Must demonstrate using the French Catheter", critical: false },
      { objective: "Inserts catheter with hole uncovered: Suction Tubing", notes: "Must demonstrate using the Suction Tubing", critical: false },
      { objective: "Insert tip to the proper depth", notes: "Student should state 'as far as I can see.'", critical: false },
      { objective: "Demonstrate proper suction technique", notes: "Sweeping motion while pulling backward.", critical: false },
      { objective: "For a time frame of 'no more than 10 seconds' (unless the patient continues vomiting)", notes: "Student should mention 'I will suction for no more than 10 seconds unless the patient continues vomiting'.", critical: false },
      { objective: "Airway Reassessment", notes: "Student should check for presence of fluid or obstructions and proceed to manage the airway due to the patient being unconscious.", critical: false }
    ]
  }
];

const CreditBox = () => (
  <div className="w-6 h-6 rounded border-2 border-primary mx-auto" />
);

const SkillSheetPreview = ({ template, onClose }) => {
  const printRef = useRef();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handlePrint = () => {
    const input = printRef.current;
    const closeButton = input.querySelector('.close-button-for-print');
    if (closeButton) closeButton.style.display = 'none';

    html2canvas(input, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
    }).then((canvas) => {
      if (closeButton) closeButton.style.display = 'block';

      const imgData = canvas.toDataURL('image/png');
      
      // Use A4 paper size in landscape and mm units
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const canvasAspectRatio = canvasWidth / canvasHeight;

      // Calculate the image dimensions to fit the page, maintaining aspect ratio
      let imgWidth = pdfWidth;
      let imgHeight = imgWidth / canvasAspectRatio;

      // If the calculated height is greater than the page height,
      // recalculate width based on height to fit within the page
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = imgHeight * canvasAspectRatio;
      }
      
      // Center the image on the page
      const x = (pdfWidth - imgWidth) / 2;
      const y = (pdfHeight - imgHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`${template.name || 'skill-sheet'}.pdf`);
    }).catch(err => {
        console.error("Error generating PDF:", err);
        if (closeButton) closeButton.style.display = 'block';
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-bg rounded-xl shadow-lg p-6 w-full max-w-4xl border border-primary max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div ref={printRef} className="p-4 bg-white">
          <button
            className="absolute top-2 right-2 text-xl text-primary hover:text-red-500 p-1 rounded-full close-button-for-print"
            onClick={onClose}
            aria-label="Close Preview"
          >
            ×
          </button>
          <h2 className="text-2xl font-bold mb-2 text-primary text-center">{template.name || '(Untitled)'}</h2>
          {template.scenarioCategory && <h3 className="text-lg font-semibold mb-2 text-gray-600 text-center">({template.scenarioCategory})</h3>}
          <div className="mb-4 text-text/70 text-sm">{template.description}</div>
          <table className="w-full editor-table preview-table">
            <thead>
              <tr className="bg-gradient-to-r from-primary/80 to-secondary/80 text-bg">
                <th style={{ width: '3rem' }} className="p-2 text-center">#</th>
                <th className="p-2">Objective</th>
                <th className="p-2">Instructor Notes</th>
                <th style={{ width: '5rem' }} className="p-2 text-center">Critical</th>
                <th style={{ width: '5rem' }} className="p-2 text-center">Full</th>
                <th style={{ width: '5rem' }} className="p-2 text-center">Half</th>
                <th style={{ width: '5rem' }} className="p-2 text-center">None</th>
              </tr>
            </thead>
            <tbody>
              {template.rows && template.rows.length > 0 ? template.rows.map((row, idx) => (
                <tr key={idx} className={`group relative ${row.critical ? 'critical-row' : ''}`}>
                  <td className="p-2 align-middle text-center">{idx + 1}</td>
                  <td className={`p-2 align-middle${row.critical ? ' font-bold' : ''}`}>{row.objective}</td>
                  <td className="p-2 align-middle text-xs italic">{row.notes}</td>
                  <td className="p-2 align-middle text-center" style={{ width: '5rem' }}>
                    {row.critical && (
                      <div className="flex items-center justify-center w-full h-full">
                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                      </div>
                    )}
                  </td>
                  <td className="p-2 align-middle text-center"><CreditBox /></td>
                  <td className="p-2 align-middle text-center"><CreditBox /></td>
                  <td className="p-2 align-middle text-center"><CreditBox /></td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="p-2 text-center text-text/60">No rows defined.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-4 mt-4 px-4 pb-4">
            <button 
                onClick={handlePrint} 
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
                Print to PDF
            </button>
        </div>
      </div>
    </div>
  );
};

const SkillSheetBuilder = () => {
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [template, setTemplate] = useState(defaultTemplate);
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]); // For listing all templates
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]); // [{id, name, color, sheets: [templateIds], expanded: bool}]
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryEdit, setCategoryEdit] = useState(null); // {id, name, color}
  const [categoryNameInput, setCategoryNameInput] = useState("");
  const [categoryColorInput, setCategoryColorInput] = useState("#3b82f6");
  const [sortMode, setSortMode] = useState("alpha"); // or 'custom'
  const [deleteModal, setDeleteModal] = useState({ open: false, onConfirm: null, message: '' });
  const hasLoaded = useRef(false);
  const [openPaletteId, setOpenPaletteId] = useState(null); // Track which palette is open
  const [palettePreview, setPalettePreview] = useState({}); // { [catId]: color }
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, field }
  const [tempCellValue, setTempCellValue] = useState('');
  const [objectiveWidth, setObjectiveWidth] = useState(450); // Initial width
  const resizingRef = useRef({ isResizing: false, startX: 0, startWidth: 0 });
  const [pdfTemplate, setPdfTemplate] = useState(null);
  const printRef = useRef();

  // This effect triggers the PDF generation when pdfTemplate is set
  useEffect(() => {
    if (pdfTemplate && printRef.current) {
      generatePdf(printRef.current, pdfTemplate.name).then(() => {
        setPdfTemplate(null); // Clear after generation
      });
    }
  }, [pdfTemplate]);

  const generatePdf = (element, name) => {
    return new Promise((resolve, reject) => {
      if (!element) {
        reject(new Error("Element to print is not available."));
        return;
      }
      html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const canvasAspectRatio = canvas.width / canvas.height;
        let imgWidth = pdfWidth;
        let imgHeight = imgWidth / canvasAspectRatio;
        if (imgHeight > pdfHeight) {
          imgHeight = pdfHeight;
          imgWidth = imgHeight * canvasAspectRatio;
        }
        const x = (pdfWidth - imgWidth) / 2;
        const y = (pdfHeight - imgHeight) / 2;
        pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
        pdf.save(`${name || 'skill-sheet'}.pdf`);
        resolve();
      }).catch(err => {
        console.error("Error generating PDF:", err);
        reject(err);
      });
    });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    resizingRef.current = {
      isResizing: true,
      startX: e.clientX,
      startWidth: objectiveWidth,
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current.isResizing) return;
    const deltaX = e.clientX - resizingRef.current.startX;
    const newWidth = resizingRef.current.startWidth + deltaX;
    if (newWidth >= 150 && newWidth <= 1000) { // Min and max width constraints
      setObjectiveWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    resizingRef.current.isResizing = false;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleEdit = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setTempValue(currentValue || '');
  };

  const handleConfirm = () => {
    if (editingField) {
      setTemplate(t => ({ ...t, [editingField]: tempValue }));
    }
    setEditingField(null);
    setTempValue('');
  };

  const handleCellEdit = (rowIndex, field, value) => {
    setEditingCell({ rowIndex, field });
    setTempCellValue(value || '');
  };

  const handleCellConfirm = () => {
    if (!editingCell) return;
    const { rowIndex, field } = editingCell;
    handleRowChange(rowIndex, field, tempCellValue);
    setEditingCell(null);
    setTempCellValue('');
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setTempCellValue('');
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  // Load categories from Firestore on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const ref = doc(db, 'skillSheetCategories', CATEGORIES_DOC_ID);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          let loaded = snap.data().categories || [];
          // Normalize sheets property to always be an array
          loaded = loaded.map(cat => ({
            ...cat,
            sheets: Array.isArray(cat.sheets)
              ? cat.sheets
              : cat.sheets
                ? Object.values(cat.sheets)
                : [],
          }));
          console.log('[SkillSheetBuilder] Loaded categories from Firestore (normalized):', loaded);
          setCategories(loaded);
        } else {
          console.log('[SkillSheetBuilder] No categories document found in Firestore.');
        }
      } catch (e) {
        console.error('Failed to load categories:', e);
      } finally {
        hasLoaded.current = true;
      }
    };
    fetchCategories();
  }, []);

  // Persist categories to Firestore whenever they change (but not on initial load)
  useEffect(() => {
    const saveCategories = async () => {
      try {
        console.log('[SkillSheetBuilder] Saving categories to Firestore:', categories);
        const ref = doc(db, 'skillSheetCategories', CATEGORIES_DOC_ID);
        await setDoc(ref, { categories });
        console.log('[SkillSheetBuilder] Categories saved successfully.');
      } catch (e) {
        console.error('[SkillSheetBuilder] Failed to save categories:', e);
      }
    };
    if (Array.isArray(categories) && hasLoaded.current) {
      saveCategories();
    }
  }, [categories]);

  // Fetch all templates if no templateId
  useEffect(() => {
    if (templateId) return;
    setListLoading(true);
    const fetchTemplates = async () => {
      const snap = await getDocs(collection(db, 'skillSheetTemplates'));
      setTemplates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setListLoading(false);
    };
    fetchTemplates();
  }, [templateId]);

  // Fetch template by ID if templateId exists
  useEffect(() => {
    if (!templateId) return;
    const fetchTemplate = async () => {
      setLoading(true);
      const ref = doc(db, 'skillSheetTemplates', templateId);
      const snap = await getDoc(ref);
      if (snap.exists()) setTemplate(snap.data());
      setLoading(false);
    };
    fetchTemplate();
  }, [templateId]);

  const handleDelete = async (id) => {
    setDeleteModal({
      open: true,
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      onConfirm: async () => {
        await deleteDoc(doc(db, 'skillSheetTemplates', id));
        setTemplates(templates => templates.filter(t => t.id !== id));
        setDeleteModal({ open: false, onConfirm: null, message: '' });
      }
    });
  };

  const importTemplates = async () => {
    setImporting(true);
    const existing = (await getDocs(collection(db, 'skillSheetTemplates'))).docs.map(doc => doc.data().name);
    for (const t of defaultTemplates) {
      if (!existing.includes(t.name)) {
        await addDoc(collection(db, 'skillSheetTemplates'), t);
      }
    }
    setImporting(false);
    setListLoading(true);
    const snap = await getDocs(collection(db, 'skillSheetTemplates'));
    setTemplates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setListLoading(false);
    alert('Default templates imported!');
  };

  if (!currentUser || (userRole !== 'Admin' && userRole !== 'owner')) {
    return <div className="p-8 text-center text-lg">Unauthorized</div>;
  }

  // --- Drag and Drop Handlers ---
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    // Drag from unassigned to category
    if (source.droppableId === 'unassigned' && destination.droppableId.startsWith('cat-')) {
      const catId = destination.droppableId.replace('cat-', '');
      setCategories(cs => cs.map(cat =>
        cat.id === catId && !cat.sheets.includes(draggableId)
          ? { ...cat, sheets: [...cat.sheets, draggableId] }
          : cat
      ));
      return;
    }
  };

  // --- Remove skill from category ---
  const removeSkillFromCategory = (catId, tid) => {
    setCategories(cs => cs.map(cat =>
      cat.id === catId
        ? { ...cat, sheets: cat.sheets.filter(id => id !== tid) }
        : cat
    ));
  };

  // Show template list if no templateId
  if (!templateId) {
    const assignedIds = categories.flatMap(cat => cat.sheets || []);
    const unassignedTemplateIds = templates
      .filter(t => !assignedIds.includes(t.id))
      .filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
      )
      .map(t => t.id);

    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="min-h-screen bg-bg text-text flex flex-col items-center py-8">
          <h1 className="text-2xl font-bold mb-4 text-primary">Skill Sheet Builder</h1>
          <div className="w-full max-w-5xl bg-table p-6 rounded-xl border border-primary mb-6 relative">
            {/* Unassigned sidebar */}
            <Droppable droppableId="unassigned">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="w-48 flex-shrink-0 bg-white/80 border border-gray-200 rounded-xl p-2 mr-4 min-h-[60px] float-left"
                  style={{ background: snapshot.isDraggingOver ? '#dbeafe' : undefined }}
                >
                  <div className="font-semibold mb-2 text-center">Unassigned</div>
                  {unassignedTemplateIds.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center">No unassigned sheets</div>
                  ) : (
                    <ul className="space-y-2">
                      {unassignedTemplateIds.map((tid, idx) => {
                        const t = templates.find(tt => tt.id === tid);
                        if (!t) return null;
                        return (
                        <Draggable key={tid} draggableId={tid} index={idx}>
                            {(provided, snapshot) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`bg-gray-100 rounded p-2 text-xs cursor-pointer hover:bg-gray-200 ${snapshot.isDragging ? 'bg-blue-200' : ''}`}
                                onClick={() => navigate(`/skill-sheet-builder/${tid}`)}
                              >
                                {t.name || '(Untitled)'}
                              </li>
                            )}
                        </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ul>
                  )}
                </div>
              )}
            </Droppable>
            {/* Main content */}
            <div className="flex-1 overflow-hidden">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <button
                  className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
                  onClick={() => setShowCategoryModal(true)}
                >
                  + Category
                </button>
                <button
                  className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
                  onClick={() => navigate('/skill-sheet-builder/new')}
                >
                  + New Template
                </button>
                <input
                  type="text"
                  placeholder="Search templates..."
                  className="border rounded px-2 py-1 ml-auto"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <select className="border rounded px-2 py-1" value={sortMode} onChange={e => setSortMode(e.target.value)}>
                  <option value="alpha">Sort: A-Z</option>
                  <option value="custom">Sort: Custom</option>
                </select>
              </div>
              {/* Category list */}
              <div className="space-y-4">
                {categories.length === 0 && <div className="text-gray-400 italic">No categories yet.</div>}
                {categories.map(cat => {
                  // Determine preview color for border/text
                  const previewColor = palettePreview[cat.id] || cat.color || '#3b82f6';
                  return (
                    <Droppable key={cat.id} droppableId={`cat-${cat.id}`}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="border rounded-xl p-2 bg-white/90 shadow relative category-container"
                          style={{ borderColor: previewColor, background: snapshot.isDraggingOver ? '#bbf7d0' : undefined, color: previewColor, transition: 'color 0.2s, border-color 0.2s' }}
                        >
                          <div
                            className="flex items-center w-full text-center font-semibold text-lg justify-center cursor-pointer select-none category-header"
                            style={{ color: previewColor, transition: 'color 0.2s' }}
                            role="button"
                            tabIndex={0}
                            onClick={e => {
                              if (e.target.closest('.palette-trigger')) return;
                              setCategories(cs => cs.map(c => c.id === cat.id ? { ...c, expanded: !c.expanded } : c));
                            }}
                            onKeyDown={e => {
                              if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.palette-trigger')) {
                                setCategories(cs => cs.map(c => c.id === cat.id ? { ...c, expanded: !c.expanded } : c));
                              }
                            }}
                          >
                            <span className="flex-1 text-center category-title">{cat.name}</span>
                            {/* Plain circle color picker trigger */}
                            <div className="palette-trigger ml-2 flex items-center relative">
                              <svg
                                viewBox="0 0 24 24"
                                className="w-6 h-6 cursor-pointer"
                                style={{ display: 'block' }}
                                onClick={e => {
                                  e.stopPropagation();
                                  setOpenPaletteId(cat.id === openPaletteId ? null : cat.id);
                                  setPalettePreview({});
                                }}
                                title="Change category color"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="9"
                                  fill={palettePreview[cat.id] || cat.color || '#3b82f6'}
                                  stroke="#888"
                                  strokeWidth="1.5"
                                />
                              </svg>
                              {/* Color picker popover */}
                              {openPaletteId === cat.id && (
                                <div
                                  className="absolute z-50 left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg px-2 py-1 grid grid-cols-3 gap-1 min-w-[110px] justify-center"
                                  style={{ top: '100%' }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  {[
                                    "#3b82f6", "#ef4444", "#10b981",
                                    "#f59e42", "#a78bfa", "#bfa16c",
                                    "#eab308", "#64748b", "#000"
                                  ].map(color => (
                                    <button
                                      key={color}
                                      type="button"
                                      className={`w-6 h-6 rounded-full border-2 transition-all duration-100 focus:outline-none focus:ring-2 focus:ring-primary ${color === (palettePreview[cat.id] || cat.color || '#3b82f6') ? 'ring-2 ring-primary border-primary' : 'border-gray-300'}`}
                                      style={{
                                        background: color,
                                        boxShadow: color === (palettePreview[cat.id] || cat.color || '#3b82f6') ? '0 0 0 2px #3b82f6' : undefined,
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={() => setPalettePreview({ [cat.id]: color })}
                                      onMouseLeave={() => setPalettePreview({})}
                                      onClick={() => {
                                        setCategories(cs => cs.map(c => c.id === cat.id ? { ...c, color } : c));
                                        setOpenPaletteId(null);
                                        setPalettePreview({});
                                      }}
                                      aria-label={`Set color ${color}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Edit icon */}
                            <button className="ml-2" onClick={e => { e.stopPropagation(); setCategoryEdit(cat); setCategoryNameInput(cat.name); setCategoryColorInput(cat.color || '#3b82f6'); setShowCategoryModal(true); }} title="Edit Category">
                              <svg className="w-5 h-5 text-gray-500 hover:text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15.232 5.232l3.536 3.536M9 13l6-6 3 3-6 6H9v-3z" /></svg>
                            </button>
                            {/* Trash icon */}
                            <button className="ml-2" onClick={e => { e.stopPropagation(); setCategoryEdit(cat); setDeleteModal({ open: true, message: `Delete category "${cat.name}"? This cannot be undone.`, onConfirm: () => { setCategories(cs => cs.filter(c => c.id !== cat.id)); setDeleteModal({ open: false, onConfirm: null, message: '' }); setCategoryEdit(null); } }); }} title="Delete Category">
                              <svg className="w-5 h-5 text-red-500 hover:text-red-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                          {cat.expanded && (
                            <ul className="mt-2 space-y-1">
                              {(cat.sheets || []).map((tid, idx) => {
                                const t = templates.find(tt => tt.id === tid);
                                if (!t) return null;
                                return (
                                  <li key={tid} className="bg-gray-100 rounded p-2 text-xs flex items-center gap-2">
                                    <span className="flex-1 cursor-pointer hover:underline" onClick={() => navigate(`/skill-sheet-builder/${tid}`)}>{t.name || '(Untitled)'}</span>
                                    <button
                                      className="text-red-500 hover:text-red-700 font-bold text-lg px-2"
                                      title="Remove from category"
                                      onClick={() => removeSkillFromCategory(cat.id, tid)}
                                      aria-label="Remove skill from category"
                                    >
                                      &minus;
                                    </button>
                                  </li>
                                );
                              })}
                              {(cat.sheets || []).length === 0 && <li className="text-xs text-gray-400 italic">No sheets in this category.</li>}
                            </ul>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </div>
            {/* Category modal */}
            {showCategoryModal && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) setShowCategoryModal(false); }}>
                <div className="bg-white rounded-xl p-6 w-full max-w-xs relative">
                  <button onClick={() => setShowCategoryModal(false)} className="absolute top-2 right-2 text-xl font-bold">×</button>
                  <h2 className="text-lg font-bold mb-4">{categoryEdit ? 'Edit' : 'New'} Category</h2>
                  <label className="block mb-2 font-semibold">Name:
                    <input className="block w-full border rounded px-2 py-1 mt-1 mb-4" value={categoryNameInput} onChange={e => setCategoryNameInput(e.target.value)} />
                  </label>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center border-2 border-gray-300" style={{ boxSizing: 'content-box' }}>
                      <input type="color" className="w-14 h-14 p-0 border-0 bg-transparent cursor-pointer" value={categoryColorInput} onChange={e => setCategoryColorInput(e.target.value)} style={{ borderRadius: '50%', transform: 'scale(1.2)', minWidth: '100%', minHeight: '100%' }} />
                    </div>
                    <button className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200 flex-1" onClick={() => {
                      if (!categoryNameInput.trim()) return;
                      if (categoryEdit) {
                        const newCategories = categories.map(c => c.id === categoryEdit.id ? { ...c, name: categoryNameInput, color: categoryColorInput } : c);
                        console.log('[SkillSheetBuilder] Updating category:', newCategories);
                        setCategories(newCategories);
                      } else {
                        const newCategories = [...categories, { id: Date.now().toString(), name: categoryNameInput, color: categoryColorInput, sheets: [], expanded: true }];
                        console.log('[SkillSheetBuilder] Adding new category:', newCategories);
                        setCategories(newCategories);
                      }
                      setShowCategoryModal(false);
                      setCategoryEdit(null);
                      setCategoryNameInput("");
                      setCategoryColorInput("#3b82f6");
                    }}>{categoryEdit ? 'Save' : 'Add'} Category</button>
                  </div>
                </div>
              </div>
            )}
            {/* Add custom delete modal for categories */}
            {deleteModal && deleteModal.open && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-xs border-2 border-red-500 shadow-xl relative">
                  <h2 className="text-xl font-bold text-red-600 mb-4">Confirm Delete</h2>
                  <div className="mb-6 text-gray-700">{deleteModal.message}</div>
                  <div className="flex gap-4 justify-end">
                    <button className="btn-raised bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 font-semibold" onClick={() => setDeleteModal({ open: false, onConfirm: null, message: '' })}>Cancel</button>
                    <button className="btn-raised bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 font-semibold" onClick={() => { deleteModal.onConfirm && deleteModal.onConfirm(); }}>Delete</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>
    );
  }

  const handleRowChange = (idx, field, value) => {
    setTemplate(t => ({
      ...t,
      rows: t.rows.map((row, i) => i === idx ? { ...row, [field]: value } : row),
    }));
  };

  const addRow = () => setTemplate(t => ({ ...t, rows: [...t.rows, { objective: '', notes: '', critical: false }] }));
  const removeRow = idx => setTemplate(t => ({ ...t, rows: t.rows.filter((_, i) => i !== idx) }));

  const moveRowUp = idx => {
    if (idx === 0) return;
    setTemplate(t => {
      const newRows = [...t.rows];
      const temp = newRows[idx - 1];
      newRows[idx - 1] = newRows[idx];
      newRows[idx] = temp;
      return { ...t, rows: newRows };
    });
  };

  const moveRowDown = idx => {
    if (idx === template.rows.length - 1) return;
    setTemplate(t => {
      const newRows = [...t.rows];
      const temp = newRows[idx + 1];
      newRows[idx + 1] = newRows[idx];
      newRows[idx] = temp;
      return { ...t, rows: newRows };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      let ref;
      if (templateId) {
        ref = doc(db, 'skillSheetTemplates', templateId);
        await setDoc(ref, template);
      } else {
        ref = await addDoc(collection(db, 'skillSheetTemplates'), template);
        navigate(`/skill-sheet-builder/${ref.id}`);
      }
      setMessage('Saved!');
    } catch (e) {
      console.error('Error saving template:', e);
      setMessage('Error saving: ' + (e.message || e.code || 'Unknown error'));
    }
    setSaving(false);
  };

  // Duplicate template handler
  const handleDuplicate = async () => {
    if (!template.name.trim()) {
      alert('Please enter a template name before duplicating');
      return;
    }
    
    try {
      const newName = `${template.name} (Copy)`;
      
      // Create a copy without the id field
      const { id, ...templateWithoutId } = template;
      
      const duplicatedTemplate = {
        ...templateWithoutId,
        name: newName,
        createdBy: currentUser?.email || 'Unknown',
        creationDate: new Date().toLocaleDateString(),
        lastModified: serverTimestamp(),
      };
      
      // Save the duplicated template to Firebase
      const docRef = await addDoc(collection(db, 'skillSheetTemplates'), duplicatedTemplate);
      
      // Navigate to edit the new template
      navigate(`/skill-sheet-builder/${docRef.id}`);
      
      setMessage(`Template duplicated as "${newName}"`);
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Error duplicating template: ' + error.message);
    }
  };

  const handleRowDragEnd = (result) => {
    if (!result.destination) return;
    
    // Validate that we're working with the correct droppable
    if (result.source.droppableId !== 'skillsheet-builder-rows' || result.destination.droppableId !== 'skillsheet-builder-rows') {
      return;
    }

    const newRows = Array.from(template.rows);
    const [reorderedItem] = newRows.splice(result.source.index, 1);
    newRows.splice(result.destination.index, 0, reorderedItem);

    setTemplate(t => ({ ...t, rows: newRows }));
  };

  // This is the main component when a template is being edited or created
  return (
    <DragDropContext onDragEnd={handleRowDragEnd}>
      <div className="min-h-screen bg-bg text-text flex flex-col items-center py-8">
        <h1 className="text-2xl font-bold mb-4 text-primary">Skill Sheet Builder</h1>
        <div className="w-full max-w-6xl bg-table p-6 rounded-xl border border-primary mb-6 shadow-lg">
          

          {['name', 'scenarioCategory', 'description'].map(field => {
            const labels = { name: 'Template Name', scenarioCategory: 'Scenario Category', description: 'Description' };
            const isEditing = editingField === field;
            return (
              <div key={field} className="mb-4 flex items-start">
                <label className="w-1/4 pt-2 font-semibold text-sm text-gray-700">{labels[field]}:</label>
                <div className="w-3/4">
                  {isEditing ? (
                    <div>
                      <input 
                        className="block w-full border rounded px-2 py-1 bg-white"
                        value={tempValue} 
                        onChange={e => setTempValue(e.target.value)}
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button onClick={handleConfirm} className="btn-add text-white px-2 py-1 rounded shadow text-lg font-bold">✓</button>
                        <button onClick={handleCancel} className="bg-gray-200 text-gray-700 px-2 py-1 rounded shadow hover:bg-gray-300 text-lg font-bold">✕</button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="p-2 border border-gray-200 hover:border-blue-400 cursor-pointer rounded min-h-[2.5rem] bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => handleEdit(field, template[field])}
                    >
                      {template[field] || <span className="text-gray-400 italic">Click to add {labels[field].toLowerCase()}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <table className="editor-table">
            <thead>
              <tr className="bg-gradient-to-r from-primary/80 to-secondary/80 text-bg">
                <th style={{ width: '2rem' }}></th>
                <th style={{ width: '2rem' }}>#</th>
                <th style={{ width: `${objectiveWidth}px`, position: 'relative' }}>
                  Objective
                  <div 
                    className="resize-handle"
                    onMouseDown={handleMouseDown}
                  />
                </th>
                <th>Instructor Notes</th>
                <th style={{ width: '5rem' }}>Critical?</th>
              </tr>
            </thead>
            <Droppable droppableId="skillsheet-builder-rows">
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {template.rows.map((row, idx) => (
                    <Draggable key={idx} draggableId={`row-${idx}`} index={idx}>
                      {(provided) => (
                        <tr {...provided.draggableProps} ref={provided.innerRef} className="group relative">
                          <td {...provided.dragHandleProps} className="cursor-grab text-center">⠿</td>
                          <td className="text-center">{idx + 1}</td>
                          <td style={{ width: `${objectiveWidth}px` }}>
                            {editingCell && editingCell.rowIndex === idx && editingCell.field === 'objective' ? (
                              <div className="flex items-center gap-1">
                                <textarea className="editor-input" value={tempCellValue} onChange={e => setTempCellValue(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleCellConfirm()} />
                                <div className="flex flex-col gap-1">
                                  <button onClick={handleCellConfirm} className="text-green-500 hover:text-green-700"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg></button>
                                  <button onClick={handleCellCancel} className="text-red-500 hover:text-red-700"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg></button>
                                </div>
                              </div>
                            ) : (
                              <div className="p-1 min-h-[2rem] cursor-pointer" onClick={() => handleCellEdit(idx, 'objective', row.objective)}>
                                {row.objective || <span className="text-gray-400 italic">Click to edit</span>}
                              </div>
                            )}
                          </td>
                          <td>
                            {editingCell && editingCell.rowIndex === idx && editingCell.field === 'notes' ? (
                              <div className="flex items-center gap-1">
                                <textarea className="editor-input" value={tempCellValue} onChange={e => setTempCellValue(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleCellConfirm()} />
                                <div className="flex flex-col gap-1">
                                  <button onClick={handleCellConfirm} className="text-green-500 hover:text-green-700"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg></button>
                                  <button onClick={handleCellCancel} className="text-red-500 hover:text-red-700"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg></button>
                                </div>
                              </div>
                            ) : (
                              <div className="p-1 min-h-[2rem] cursor-pointer" onClick={() => handleCellEdit(idx, 'notes', row.notes)}>
                                {row.notes || <span className="text-gray-400 italic">Click to edit</span>}
                              </div>
                            )}
                          </td>
                          <td className="text-center"><input type="checkbox" checked={row.critical} onChange={e => handleRowChange(idx, 'critical', e.target.checked)} /></td>
                          <td className="absolute right-0 h-full flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                                type="button" 
                                className="delete-button text-red-500 hover:text-red-700"
                                onClick={() => removeRow(idx)} 
                                disabled={template.rows.length === 1}
                                title="Delete Row"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                </svg>
                            </button>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
            )}
          </Droppable>
        </table>
        <div className="flex justify-end gap-4 mt-6">
            <button type="button" className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200" onClick={addRow}>+ Row</button>
            <button type="button" className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded shadow font-semibold transition duration-200" onClick={() => setPreviewTemplate(template)}>Preview</button>
            {templateId && (
              <button 
                type="button" 
                className="bg-orange-500 hover:bg-orange-700 text-white px-4 py-2 rounded shadow font-semibold transition duration-200"
                onClick={handleDuplicate}
              >
                Duplicate
              </button>
            )}
            <button type="button" className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Template'}</button>
            <button 
              type="button" 
              className="bg-green-600 hover:bg-green-800 text-white px-4 py-2 rounded shadow font-semibold transition duration-200 flex items-center gap-2"
              onClick={() => setPdfTemplate(template)} // Set the template to be printed
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              PDF
            </button>
            {message && <div className="mt-2 text-green-600 font-semibold">{message}</div>}
        </div>
      </div>
      {previewTemplate && <SkillSheetPreview template={previewTemplate} onClose={() => setPreviewTemplate(null)} />}
      {/* Hidden preview for direct PDF generation */}
      {pdfTemplate && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <div ref={printRef} className="p-4 bg-white">
                <h2 className="text-2xl font-bold mb-2 text-primary text-center">{pdfTemplate.name || '(Untitled)'}</h2>
                {pdfTemplate.scenarioCategory && <h3 className="text-lg font-semibold mb-2 text-gray-600 text-center">({pdfTemplate.scenarioCategory})</h3>}
                <div className="mb-4 text-text/70 text-sm">{pdfTemplate.description}</div>
                <table className="w-full editor-table preview-table">
                    <thead>
                        <tr className="bg-gradient-to-r from-primary/80 to-secondary/80 text-bg">
                            <th style={{ width: '3rem' }} className="p-2 text-center">#</th>
                            <th className="p-2">Objective</th>
                            <th className="p-2">Instructor Notes</th>
                            <th style={{ width: '5rem' }} className="p-2 text-center">Critical</th>
                            <th style={{ width: '5rem' }} className="p-2 text-center">Full</th>
                            <th style={{ width: '5rem' }} className="p-2 text-center">Half</th>
                            <th style={{ width: '5rem' }} className="p-2 text-center">None</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pdfTemplate.rows && pdfTemplate.rows.length > 0 ? pdfTemplate.rows.map((row, idx) => (
                            <tr key={idx} className={`group relative ${row.critical ? 'critical-row' : ''}`}>
                                <td className="p-2 align-middle text-center">{idx + 1}</td>
                                <td className={`p-2 align-middle${row.critical ? ' font-bold' : ''}`}>{row.objective}</td>
                                <td className="p-2 align-middle text-xs italic">{row.notes}</td>
                                <td className="p-2 align-middle text-center" style={{ width: '5rem' }}>
                                    {row.critical && (
                                        <div className="flex items-center justify-center w-full h-full">
                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                                        </div>
                                    )}
                                </td>
                                <td className="p-2 align-middle text-center"><CreditBox /></td>
                                <td className="p-2 align-middle text-center"><CreditBox /></td>
                                <td className="p-2 align-middle text-center"><CreditBox /></td>
                            </tr>
                        )) : (
                            <tr><td colSpan={7} className="p-2 text-center text-text/60">No rows defined.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      )}
    </div>
    </DragDropContext>
  );
};

export default SkillSheetBuilder;
